import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { onboardingService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const DEFAULT_META_APP_ID = '690735040099659';
const DEFAULT_META_CONFIG_ID = '1563935541405382';

const STEP = {
  CONFIRM_CLINIC: 'confirm_clinic',
  EMAIL: 'email',
  PASSWORD: 'password',
  EMBEDDED_SIGNUP: 'embedded_signup',
  QUESTIONNAIRE: 'questionnaire',
  COMPLETED: 'completed',
};

const STEP_ORDER = [
  STEP.CONFIRM_CLINIC,
  STEP.EMAIL,
  STEP.PASSWORD,
  STEP.EMBEDDED_SIGNUP,
  STEP.QUESTIONNAIRE,
  STEP.COMPLETED,
];

const Onboarding = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [onboarding, setOnboarding] = useState(null);
  const [currentStep, setCurrentStep] = useState(STEP.CONFIRM_CLINIC);
  const [stepVisible, setStepVisible] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [embeddedSaved, setEmbeddedSaved] = useState(false);
  const [exchangeResult, setExchangeResult] = useState(null);
  const [questionnaire, setQuestionnaire] = useState({
    business_description: '',
    opening_hours: '',
    services_provided: '',
    disclose_price: '',
    service_costs: '',
    references: '',
    allow_ai_training: '',
  });

  const sessionWabaIdRef = useRef(null);
  const sessionPhoneNumberIdRef = useRef(null);
  const processingCodeRef = useRef(false);

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  }, []);

  const appId = process.env.REACT_APP_META_WABA_APP_ID || DEFAULT_META_APP_ID;
  const configId = process.env.REACT_APP_META_WABA_APP_CONFIG_ID || DEFAULT_META_CONFIG_ID;

  const loadOnboarding = useCallback(async () => {
    if (!token) {
      setError(t('onboarding.error.missingToken', 'Missing onboarding token.'));
      setLoading(false);
      return;
    }
    try {
      const data = await onboardingService.getByToken(token);
      setOnboarding(data);
      setCurrentStep(data.current_step || STEP.CONFIRM_CLINIC);
      if (data.answers?.admin?.email) {
        setEmail(data.answers.admin.email);
      }
      if (data.answers) {
        setQuestionnaire((prev) => ({
          ...prev,
          ...data.answers,
        }));
      }
    } catch (e) {
      setError(e?.response?.data?.detail || t('onboarding.error.invalidToken', 'Invalid or expired onboarding token.'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void loadOnboarding();
  }, [loadOnboarding]);

  useEffect(() => {
    setStepVisible(false);
    const timer = setTimeout(() => setStepVisible(true), 60);
    return () => clearTimeout(timer);
  }, [currentStep]);

  const persistStep = useCallback(
    async (step) => {
      await onboardingService.updateStep({ token, current_step: step });
      setCurrentStep(step);
    },
    [token]
  );

  const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);

  const onConfirmClinic = async () => {
    setSubmitting(true);
    try {
      await persistStep(STEP.EMAIL);
    } finally {
      setSubmitting(false);
    }
  };

  const onSaveEmail = async () => {
    if (!isValidEmail(email)) {
      toast.error(t('onboarding.error.invalidEmail', 'Please provide a valid email address.'));
      return;
    }
    setSubmitting(true);
    try {
      await persistStep(STEP.PASSWORD);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save email.');
    } finally {
      setSubmitting(false);
    }
  };

  const onCreateAdmin = async () => {
    if (!isValidEmail(email)) {
      toast.error(t('onboarding.error.invalidEmail', 'Please provide a valid email address.'));
      return;
    }
    if (password.length < 6) {
      toast.error(t('onboarding.error.passwordLength', 'Password must be at least 6 characters long.'));
      return;
    }
    setSubmitting(true);
    try {
      await onboardingService.createAdmin({ token, email, password });
      setCurrentStep(STEP.EMBEDDED_SIGNUP);
      toast.success(t('onboarding.admin.created', 'Admin account created.'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create clinic admin.');
    } finally {
      setSubmitting(false);
    }
  };

  const loadFacebookSdk = useCallback(() => {
    const initialize = () => {
      if (!window.FB) return;
      window.FB.init({
        appId,
        cookie: true,
        xfbml: true,
        version: 'v20.0',
      });
    };

    if (window.FB) {
      initialize();
      return;
    }

    window.fbAsyncInit = initialize;
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    }
  }, [appId]);

  const processCode = useCallback(
    async (code) => {
      if (!code || processingCodeRef.current) return;
      processingCodeRef.current = true;
      try {
        const redirect_uri = window.location.origin + window.location.pathname;
        const result = await onboardingService.exchangeAuthorizationCode({
          token,
          code,
          redirect_uri,
        });
        setExchangeResult(result);
        await onboardingService.saveEmbeddedSignup({
          token,
          waba_id: sessionWabaIdRef.current,
          phone_number_id: sessionPhoneNumberIdRef.current,
          access_token: result?.access_token,
          expires_in: result?.expires_in,
        });
        setEmbeddedSaved(true);
        await persistStep(STEP.QUESTIONNAIRE);
        toast.success('WhatsApp embedded signup completed.');
      } catch (e) {
        processingCodeRef.current = false;
        toast.error(e?.response?.data?.detail || 'Failed to complete embedded signup.');
      }
    },
    [persistStep, token]
  );

  useEffect(() => {
    if (currentStep !== STEP.EMBEDDED_SIGNUP) return;
    loadFacebookSdk();

    const params = new URLSearchParams(window.location.search);
    const codeFromUrl = params.get('code');
    if (codeFromUrl) {
      void processCode(codeFromUrl);
      params.delete('code');
      const query = params.toString();
      window.history.replaceState({}, document.title, query ? `${window.location.pathname}?${query}` : window.location.pathname);
    }

    const handleMessage = (event) => {
      if (event?.origin !== 'https://www.facebook.com') return;
      if (typeof event.data !== 'string') return;
      try {
        const data = JSON.parse(event.data);
        if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (data?.event === 'FINISH' || data?.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
          if (data?.data?.waba_id) sessionWabaIdRef.current = data.data.waba_id;
          if (data?.data?.phone_number_id) sessionPhoneNumberIdRef.current = data.data.phone_number_id;
        }
      } catch (_err) {
        // Ignore non-JSON events.
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentStep, loadFacebookSdk, processCode]);

  const startEmbeddedSignup = () => {
    if (!window.FB) {
      toast.error(t('onboarding.error.sdkNotReady', 'Facebook SDK is not ready.'));
      return;
    }

    window.FB.login(
      (response) => {
        const code = response?.authResponse?.code;
        if (!code) {
          toast.error(t('onboarding.error.embeddedCancelled', 'Embedded signup was cancelled or did not return a code.'));
          return;
        }
        void processCode(code);
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      }
    );
  };

  const onQuestionnaireChange = (key, value) => {
    setQuestionnaire((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmitQuestionnaire = async () => {
    if (!questionnaire.business_description || !questionnaire.opening_hours || !questionnaire.services_provided) {
      toast.error(t('onboarding.error.requiredFields', 'Please complete all required fields.'));
      return;
    }
    if (!questionnaire.disclose_price) {
      toast.error(t('onboarding.error.disclosePrice', 'Please specify whether you disclose prices.'));
      return;
    }
    if (questionnaire.disclose_price === 'yes' && !questionnaire.service_costs.trim()) {
      toast.error(t('onboarding.error.serviceCost', 'Please provide your service cost details.'));
      return;
    }
    if (!questionnaire.allow_ai_training) {
      toast.error(t('onboarding.error.aiConsent', 'Please specify AI training consent.'));
      return;
    }

    setSubmitting(true);
    try {
      await onboardingService.saveAnswers({
        token,
        answers: {
          ...questionnaire,
          admin: {
            email,
          },
          embedded_signup: {
            waba_id: sessionWabaIdRef.current || exchangeResult?.waba_id || '',
            phone_number_id: sessionPhoneNumberIdRef.current || '',
          },
        },
      });
      setCurrentStep(STEP.COMPLETED);
      toast.success(t('onboarding.completed.success', 'Onboarding completed successfully.'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save onboarding answers.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-gray-600">{t('onboarding.loading', 'Loading onboarding flow...')}</div>;
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="max-w-xl rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-700">
          <h1 className="text-lg font-semibold">{t('onboarding.invalidLink.title', 'Invalid onboarding link')}</h1>
          <p className="mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const currentStepIndex = STEP_ORDER.indexOf(currentStep);

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t('onboarding.title', 'Clinic Onboarding')}</h1>
        <p className="text-sm text-gray-600">
          {t('onboarding.stepCounter', 'Step {{current}} of {{total}}')
            .replace('{{current}}', String(Math.max(currentStepIndex + 1, 1)))
            .replace('{{total}}', String(STEP_ORDER.length - 1))}
        </p>
      </div>

      <div
        className={`rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-opacity duration-300 ${
          stepVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {currentStep === STEP.CONFIRM_CLINIC && (
          <div>
            <h2 className="text-xl font-semibold">{t('onboarding.confirmClinic.title', 'Confirm your clinic details')}</h2>
            <div className="mt-4 space-y-2 text-sm">
              <p><span className="font-medium">{t('onboarding.clinicName', 'Clinic name:')}</span> {onboarding?.clinic?.name || onboarding?.name}</p>
              <p><span className="font-medium">{t('onboarding.region', 'Region:')}</span> {onboarding?.clinic?.region || 'N/A'}</p>
            </div>
            <button
              type="button"
              onClick={onConfirmClinic}
              disabled={submitting}
              className="mt-6 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
            >
              {submitting ? t('onboarding.saving', 'Saving...') : t('onboarding.confirmClinic.button', 'Confirm and Continue')}
            </button>
          </div>
        )}

        {currentStep === STEP.EMAIL && (
          <div>
            <h2 className="text-xl font-semibold">{t('onboarding.email.title', 'Provide admin email')}</h2>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@clinic.com"
              className="mt-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={onSaveEmail}
              disabled={submitting}
              className="mt-6 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
            >
              {submitting ? t('onboarding.saving', 'Saving...') : t('onboarding.continue', 'Continue')}
            </button>
          </div>
        )}

        {currentStep === STEP.PASSWORD && (
          <div>
            <h2 className="text-xl font-semibold">{t('onboarding.password.title', 'Set admin password')}</h2>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="mt-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={onCreateAdmin}
              disabled={submitting}
              className="mt-6 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
            >
              {submitting ? t('onboarding.creating', 'Creating...') : t('onboarding.password.button', 'Create Admin and Continue')}
            </button>
          </div>
        )}

        {currentStep === STEP.EMBEDDED_SIGNUP && (
          <div>
            <h2 className="text-xl font-semibold">{t('onboarding.embedded.title', 'Connect WhatsApp (Embedded Signup)')}</h2>
            <p className="mt-2 text-sm text-gray-600">
              {t('onboarding.embedded.description', 'Use Meta Embedded Signup to connect your WhatsApp number to Nexi.')}
            </p>
            <button
              type="button"
              onClick={startEmbeddedSignup}
              disabled={submitting || embeddedSaved}
              className="mt-6 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
            >
              {embeddedSaved ? t('onboarding.embedded.connected', 'Connected') : t('onboarding.embedded.start', 'Start Embedded Signup')}
            </button>
          </div>
        )}

        {currentStep === STEP.QUESTIONNAIRE && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">{t('onboarding.questionnaire.title', 'Tell us about your business')}</h2>

            <textarea
              value={questionnaire.business_description}
              onChange={(e) => onQuestionnaireChange('business_description', e.target.value)}
              placeholder="Please describe your business to your customers..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              rows={3}
            />
            <input
              value={questionnaire.opening_hours}
              onChange={(e) => onQuestionnaireChange('opening_hours', e.target.value)}
              placeholder="What are your opening hours?"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              value={questionnaire.services_provided}
              onChange={(e) => onQuestionnaireChange('services_provided', e.target.value)}
              placeholder="What service do you provide?"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />

            <div>
              <p className="mb-2 text-sm font-medium">Are you going to disclose your price to customers?</p>
              <div className="flex gap-4 text-sm">
                <label><input type="radio" name="disclosePrice" value="yes" checked={questionnaire.disclose_price === 'yes'} onChange={(e) => onQuestionnaireChange('disclose_price', e.target.value)} /> Yes</label>
                <label><input type="radio" name="disclosePrice" value="no" checked={questionnaire.disclose_price === 'no'} onChange={(e) => onQuestionnaireChange('disclose_price', e.target.value)} /> No</label>
              </div>
            </div>

            {questionnaire.disclose_price === 'yes' && (
              <input
                value={questionnaire.service_costs}
                onChange={(e) => onQuestionnaireChange('service_costs', e.target.value)}
                placeholder="How much does your services cost?"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            )}

            <input
              value={questionnaire.references}
              onChange={(e) => onQuestionnaireChange('references', e.target.value)}
              placeholder="Do you have any references like PDF or link?"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />

            <div>
              <p className="mb-2 text-sm font-medium">Allow AI to read your history for training?</p>
              <div className="flex gap-4 text-sm">
                <label><input type="radio" name="allowAiTraining" value="yes" checked={questionnaire.allow_ai_training === 'yes'} onChange={(e) => onQuestionnaireChange('allow_ai_training', e.target.value)} /> Yes</label>
                <label><input type="radio" name="allowAiTraining" value="no" checked={questionnaire.allow_ai_training === 'no'} onChange={(e) => onQuestionnaireChange('allow_ai_training', e.target.value)} /> No</label>
              </div>
            </div>

            <button
              type="button"
              onClick={onSubmitQuestionnaire}
              disabled={submitting}
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
            >
              {submitting ? t('onboarding.submitting', 'Submitting...') : t('onboarding.questionnaire.complete', 'Complete Onboarding')}
            </button>
          </div>
        )}

        {currentStep === STEP.COMPLETED && (
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-emerald-600">{t('onboarding.completed.title', 'Onboarding Completed')}</h2>
            <p className="mt-2 text-sm text-gray-600">{t('onboarding.completed.description', 'Your clinic setup is complete. You can now start using Nexi.')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
